import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  DollarSign,
  Users,
  Clock,
  CheckCircle2,
  Banknote,
  RefreshCw,
  Filter,
  Search,
  User,
  ArrowLeft,
  Check,
  X,
  Send,
  AlertCircle,
  TrendingUp,
  Wallet,
  ChevronDown,
  Edit3,
  Save,
  Percent,
  Eye,
  Plus,
  FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Slider } from "../../components/ui/slider";
import DashboardLayout from "../../components/DashboardLayout";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "Aprobado", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  paid: { label: "Pagado", color: "bg-blue-100 text-blue-700", icon: Banknote },
  requested: { label: "Solicitado", color: "bg-purple-100 text-purple-700", icon: Send },
  processing: { label: "Procesando", color: "bg-orange-100 text-orange-700", icon: RefreshCw },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: X }
};

const AdminCommissions = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);
  const [withdrawalNote, setWithdrawalNote] = useState("");
  const [editingVendor, setEditingVendor] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState(50);
  const [commissionPreview, setCommissionPreview] = useState(null);
  const [showAddManual, setShowAddManual] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [manualCommission, setManualCommission] = useState({
    vendedor_id: "",
    amount: "",
    description: "",
    client_name: "",
    product_type: "servicio_adicional"
  });

  useEffect(() => {
    fetchData();
    fetchVendors();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/vendedor/admin/commissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/vendedor/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data.vendors || []);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const handleAddManualCommission = async () => {
    if (!manualCommission.vendedor_id || !manualCommission.amount || !manualCommission.description) {
      toast.error("Completa todos los campos requeridos");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/vendedor/admin/commissions/manual`, {
        vendedor_id: manualCommission.vendedor_id,
        amount: parseFloat(manualCommission.amount),
        description: manualCommission.description,
        client_name: manualCommission.client_name || null,
        product_type: manualCommission.product_type
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Comisión de $${manualCommission.amount} agregada`);
      setShowAddManual(false);
      setManualCommission({
        vendedor_id: "",
        amount: "",
        description: "",
        client_name: "",
        product_type: "servicio_adicional"
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar comisión");
    }
  };

  const handleApproveCommission = async (commissionId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/vendedor/admin/commissions/${commissionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Comisión aprobada");
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar comisión");
    }
  };

  const handleApproveBulk = async () => {
    if (selectedCommissions.length === 0) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/vendedor/admin/commissions/approve-bulk`, selectedCommissions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedCommissions.length} comisiones aprobadas`);
      setSelectedCommissions([]);
      fetchData();
    } catch (error) {
      toast.error("Error al aprobar comisiones");
    }
  };

  const handleProcessWithdrawal = async (withdrawalId, action) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/vendedor/admin/withdrawals/${withdrawalId}/process?action=${action}&note=${encodeURIComponent(withdrawalNote)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(action === "pay" ? "Retiro marcado como pagado" : "Retiro rechazado");
      setProcessingWithdrawal(null);
      setWithdrawalNote("");
      fetchData();
    } catch (error) {
      toast.error("Error al procesar retiro");
    }
  };

  const handleFetchCommissionPreview = async (vendorId, rate) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/vendedor/admin/vendors/${vendorId}/commission-preview?rate=${rate / 100}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCommissionPreview(response.data.previews);
    } catch (error) {
      console.error("Error fetching preview");
    }
  };

  const handleSaveCommissionRate = async () => {
    if (!editingVendor) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/vendedor/admin/vendors/${editingVendor.id}/commission-rate?rate=${newCommissionRate / 100}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Comisión actualizada a ${newCommissionRate}%`);
      setEditingVendor(null);
      fetchData();
    } catch (error) {
      toast.error("Error al actualizar comisión");
    }
  };

  const toggleCommissionSelection = (id) => {
    setSelectedCommissions(prev => 
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  const stats = data?.stats || {};
  const commissions = data?.commissions || [];
  const withdrawals = data?.pending_withdrawals || [];

  // Filter commissions
  const filteredCommissions = commissions.filter(c => {
    const matchesStatus = filterStatus === "all" || c.status === filterStatus;
    const matchesSearch = !searchTerm || 
      c.vendedor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.client_email?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Get pending commissions for bulk approve
  const pendingCommissions = filteredCommissions.filter(c => c.status === "pending");

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin" className="p-2 hover:bg-navy-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-navy-500" />
              </Link>
              <h1 className="font-heading text-3xl font-bold text-navy-900">Comisiones de Vendedores</h1>
            </div>
            <p className="text-navy-500 ml-12">Gestiona las comisiones y solicitudes de retiro</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddManual(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Comisión
            </Button>
            <Button onClick={fetchData} variant="outline" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-yellow-500" />
              <p className="text-sm text-navy-500">Pendientes</p>
            </div>
            <p className="text-2xl font-bold text-navy-900">${stats.pending?.toFixed(2) || "0.00"}</p>
            <p className="text-xs text-navy-400">{stats.pending_count || 0} comisiones</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <p className="text-sm text-navy-500">Por Pagar</p>
            </div>
            <p className="text-2xl font-bold text-emerald-600">${stats.approved?.toFixed(2) || "0.00"}</p>
            <p className="text-xs text-navy-400">{stats.approved_count || 0} aprobadas</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <Banknote className="w-4 h-4 text-blue-500" />
              <p className="text-sm text-navy-500">Pagado</p>
            </div>
            <p className="text-2xl font-bold text-navy-900">${stats.paid?.toFixed(2) || "0.00"}</p>
          </div>
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-purple-500" />
              <p className="text-sm text-navy-500">Total Generado</p>
            </div>
            <p className="text-2xl font-bold text-navy-900">${stats.total?.toFixed(2) || "0.00"}</p>
          </div>
        </div>

        {/* Pending Withdrawals Alert */}
        {withdrawals.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">
                {withdrawals.length} Solicitud(es) de Retiro Pendiente(s)
              </h3>
            </div>
            <div className="space-y-2">
              {withdrawals.map(withdrawal => (
                <div 
                  key={withdrawal.id}
                  className="bg-white rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-navy-900">{withdrawal.vendedor_name}</p>
                    <p className="text-sm text-navy-500">
                      ${withdrawal.amount?.toFixed(2)} • {withdrawal.payment_method} • {withdrawal.payment_details}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setProcessingWithdrawal(withdrawal)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Revisar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Filters & Actions */}
        <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-navy-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendientes</SelectItem>
                  <SelectItem value="approved">Aprobados</SelectItem>
                  <SelectItem value="paid">Pagados</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 max-w-xs">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar vendedor o cliente..."
                  className="pl-10"
                />
              </div>
            </div>

            {selectedCommissions.length > 0 && (
              <Button
                onClick={handleApproveBulk}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Check className="w-4 h-4 mr-2" />
                Aprobar {selectedCommissions.length} seleccionadas
              </Button>
            )}
          </div>
        </div>

        {/* Commissions Table */}
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          <div className="p-4 border-b border-navy-100 flex items-center justify-between">
            <h2 className="font-semibold text-navy-900">
              Comisiones ({filteredCommissions.length})
            </h2>
            {pendingCommissions.length > 0 && (
              <button
                onClick={() => setSelectedCommissions(
                  selectedCommissions.length === pendingCommissions.length
                    ? []
                    : pendingCommissions.map(c => c.id)
                )}
                className="text-sm text-emerald-600 hover:underline"
              >
                {selectedCommissions.length === pendingCommissions.length
                  ? "Deseleccionar todas"
                  : `Seleccionar todas pendientes (${pendingCommissions.length})`}
              </button>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-50">
                <tr>
                  <th className="w-10 p-3"></th>
                  <th className="text-left p-3 text-xs font-medium text-navy-500 uppercase">Vendedor</th>
                  <th className="text-left p-3 text-xs font-medium text-navy-500 uppercase">Cliente</th>
                  <th className="text-left p-3 text-xs font-medium text-navy-500 uppercase">Producto</th>
                  <th className="text-right p-3 text-xs font-medium text-navy-500 uppercase">Comisión</th>
                  <th className="text-center p-3 text-xs font-medium text-navy-500 uppercase">Estado</th>
                  <th className="text-center p-3 text-xs font-medium text-navy-500 uppercase">Fecha</th>
                  <th className="text-center p-3 text-xs font-medium text-navy-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-100">
                {filteredCommissions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-navy-500">
                      No hay comisiones que mostrar
                    </td>
                  </tr>
                ) : (
                  filteredCommissions.map((commission) => {
                    const StatusIcon = statusConfig[commission.status]?.icon || Clock;
                    return (
                      <tr key={commission.id} className="hover:bg-navy-50">
                        <td className="p-3">
                          {commission.status === "pending" && (
                            <input
                              type="checkbox"
                              checked={selectedCommissions.includes(commission.id)}
                              onChange={() => toggleCommissionSelection(commission.id)}
                              className="rounded border-navy-300"
                            />
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center">
                              <User className="w-4 h-4 text-navy-500" />
                            </div>
                            <div>
                              <p className="font-medium text-navy-900 text-sm">{commission.vendedor_name || "—"}</p>
                              <button
                                onClick={() => {
                                  setEditingVendor({ id: commission.vendedor_id, name: commission.vendedor_name });
                                  setNewCommissionRate(50);
                                }}
                                className="text-xs text-emerald-600 hover:underline flex items-center gap-1"
                              >
                                <Percent className="w-3 h-3" />
                                Editar comisión
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-sm text-navy-700">{commission.client_name || commission.client_email || "—"}</p>
                        </td>
                        <td className="p-3">
                          <p className="text-sm text-navy-700">{commission.product_name || commission.product_id}</p>
                        </td>
                        <td className="p-3 text-right">
                          <p className="font-bold text-emerald-600">${commission.commission?.toFixed(2)}</p>
                        </td>
                        <td className="p-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${statusConfig[commission.status]?.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig[commission.status]?.label}
                          </span>
                        </td>
                        <td className="p-3 text-center text-sm text-navy-500">
                          {new Date(commission.created_at).toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: 'short'
                          })}
                        </td>
                        <td className="p-3 text-center">
                          {commission.status === "pending" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveCommission(commission.id)}
                              className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Process Withdrawal Modal */}
      <AnimatePresence>
        {processingWithdrawal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setProcessingWithdrawal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Procesar Retiro</h2>
                    <p className="text-purple-100 text-sm">{processingWithdrawal.vendedor_name}</p>
                  </div>
                  <button onClick={() => setProcessingWithdrawal(null)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-purple-50 rounded-xl p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-purple-600 uppercase">Monto</p>
                      <p className="text-2xl font-bold text-purple-900">${processingWithdrawal.amount?.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-600 uppercase">Método</p>
                      <p className="font-semibold text-purple-900">{processingWithdrawal.payment_method}</p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-purple-200">
                    <p className="text-xs text-purple-600 uppercase">Detalles de pago</p>
                    <p className="font-medium text-purple-900">{processingWithdrawal.payment_details}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <Label>Nota (opcional)</Label>
                  <Textarea
                    value={withdrawalNote}
                    onChange={(e) => setWithdrawalNote(e.target.value)}
                    placeholder="Referencia de transferencia, observaciones..."
                    rows={2}
                    className="mt-1"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleProcessWithdrawal(processingWithdrawal.id, "reject")}
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Rechazar
                  </Button>
                  <Button
                    onClick={() => handleProcessWithdrawal(processingWithdrawal.id, "pay")}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Marcar Pagado
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Commission Rate Modal */}
      <AnimatePresence>
        {editingVendor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setEditingVendor(null)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Configurar Comisión</h2>
                    <p className="text-emerald-100 text-sm">{editingVendor.name}</p>
                  </div>
                  <button onClick={() => setEditingVendor(null)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <Label className="text-lg">Porcentaje de Comisión</Label>
                  <div className="flex items-center gap-4 mt-3">
                    <Slider
                      value={[newCommissionRate]}
                      onValueChange={(v) => {
                        setNewCommissionRate(v[0]);
                        handleFetchCommissionPreview(editingVendor.id, v[0]);
                      }}
                      max={100}
                      min={0}
                      step={5}
                      className="flex-1"
                    />
                    <div className="w-20 text-center">
                      <span className="text-3xl font-bold text-emerald-600">{newCommissionRate}%</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-navy-400 mt-1">
                    <span>0%</span>
                    <span>50%</span>
                    <span>100%</span>
                  </div>
                </div>

                {commissionPreview && (
                  <div className="bg-navy-50 rounded-xl p-4 mb-4">
                    <p className="text-sm font-medium text-navy-700 mb-3">Vista previa de ganancias:</p>
                    <div className="space-y-2">
                      {commissionPreview.map(p => (
                        <div key={p.product_id} className="flex items-center justify-between text-sm">
                          <span className="text-navy-600">{p.product_name}</span>
                          <div className="text-right">
                            <span className="font-bold text-emerald-600">${p.vendor_earns?.toFixed(2)}</span>
                            <span className="text-navy-400 text-xs ml-2">
                              (Divídelo: ${p.dividelo_keeps?.toFixed(2)})
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setEditingVendor(null)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSaveCommissionRate}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Guardar {newCommissionRate}%
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Manual Commission Modal */}
      <AnimatePresence>
        {showAddManual && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowAddManual(false)}
          >
            <motion.div
              initial={{ scale: 0.95, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-5 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="w-8 h-8" />
                    <div>
                      <h2 className="text-xl font-bold">Agregar Comisión Manual</h2>
                      <p className="text-emerald-100 text-sm">Servicios adicionales del cliente</p>
                    </div>
                  </div>
                  <button onClick={() => setShowAddManual(false)} className="p-2 hover:bg-white/20 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <Label>Vendedor *</Label>
                  <Select
                    value={manualCommission.vendedor_id}
                    onValueChange={(v) => setManualCommission({ ...manualCommission, vendedor_id: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Selecciona el vendedor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name} ({v.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Tipo de Servicio</Label>
                  <Select
                    value={manualCommission.product_type}
                    onValueChange={(v) => setManualCommission({ ...manualCommission, product_type: v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="foia">FOIA Request</SelectItem>
                      <SelectItem value="simulacro">Simulacro de Entrevista</SelectItem>
                      <SelectItem value="adelanto_cita">Adelanto de Cita</SelectItem>
                      <SelectItem value="servicio_adicional">Otro Servicio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Monto de Comisión (USD) *</Label>
                  <Input
                    type="number"
                    value={manualCommission.amount}
                    onChange={(e) => setManualCommission({ ...manualCommission, amount: e.target.value })}
                    placeholder="25.00"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Descripción *</Label>
                  <Input
                    value={manualCommission.description}
                    onChange={(e) => setManualCommission({ ...manualCommission, description: e.target.value })}
                    placeholder="Ej: FOIA FBI - Juan Pérez"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Nombre del Cliente (opcional)</Label>
                  <Input
                    value={manualCommission.client_name}
                    onChange={(e) => setManualCommission({ ...manualCommission, client_name: e.target.value })}
                    placeholder="Nombre del cliente"
                    className="mt-1"
                  />
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800">
                    <strong>Nota:</strong> Esta comisión se agregará como "Aprobada" y estará disponible 
                    inmediatamente para retiro del vendedor.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddManual(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAddManualCommission}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    disabled={!manualCommission.vendedor_id || !manualCommission.amount || !manualCommission.description}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Comisión
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  );
};

export default AdminCommissions;
